import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useUIStore } from '../stores/useUIStore';
import { springs } from '../lib/animationConfig';
import { API_ORIGIN } from '../lib/apiBase';

// ── Popular searches ────────────────────────────────────────────────────────────
const POPULAR = [
  'Kumkumadi Oil', 'Bhringraj Oil', 'Ashwagandha', 'Hair Growth', 'Face Oil',
  'Immunity Booster', 'Triphala', 'Amla', 'Stress Relief',
];

// ── Debounce hook ──────────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function SearchOverlay() {
  const { isSearchOpen, closeSearch } = useUIStore();
  const [query, setQuery]   = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 280);

  // Focus input when overlay opens
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setQuery('');
      setResults([]);
    }
    return () => { document.body.style.overflow = ''; };
  }, [isSearchOpen]);

  // ESC closes
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSearch();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [closeSearch]);

  // Fetch search results
  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const controller = new AbortController();

    fetch(`${API_ORIGIN}/api/products/search?q=${encodeURIComponent(debouncedQuery)}&limit=6`, {
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        setResults(data.products || data || []);
        setLoading(false);
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [debouncedQuery]);

  const handlePopular = useCallback((term: string) => {
    setQuery(term);
    inputRef.current?.focus();
  }, []);

  return (
    <AnimatePresence>
      {isSearchOpen && (
        <>
          {/* ── Backdrop ──────────────────────────────────────────────────── */}
          <motion.div
            className="fixed inset-0 z-[60] bg-forest-950/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeSearch}
            aria-hidden="true"
          />

          {/* ── Panel ─────────────────────────────────────────────────────── */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            className="fixed inset-x-0 top-0 z-[61] bg-cream shadow-2xl"
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={springs.panel}
          >
            <div className="section-container py-8">

              {/* Search input row */}
              <div className="flex items-center gap-4 mb-8">
                {/* Search icon */}
                <svg className="shrink-0 text-forest-400" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                </svg>

                {/* Input */}
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search ingredients, concerns, products…"
                  className="flex-1 bg-transparent font-display text-display-sm text-forest-900 placeholder:text-forest-300 placeholder:font-light focus:outline-none"
                  aria-label="Search products and ingredients"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />

                {/* Clear */}
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    className="text-forest-400 hover:text-forest-700 transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                )}

                {/* Close */}
                <button
                  onClick={closeSearch}
                  aria-label="Close search"
                  className="text-forest-400 hover:text-forest-700 transition-colors ml-2"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Divider */}
              <div className="h-px bg-cream-dark mb-6" />

              <AnimatePresence mode="wait">
                {/* Loading */}
                {loading && (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-8 flex justify-center">
                    <svg className="animate-spin w-6 h-6 text-forest-400" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </motion.div>
                )}

                {/* Live results */}
                {!loading && results.length > 0 && (
                  <motion.div key="results" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <p className="font-label text-label-sm text-forest-400 uppercase tracking-widest mb-4">Results</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {results.map((product: any) => (
                        <Link
                          key={product.id}
                          to={`/product/${product.slug}`}
                          onClick={closeSearch}
                          className="group flex items-center gap-4 p-3 rounded-xl hover:bg-cream-warm transition-colors duration-150"
                        >
                          <div className="w-14 h-14 rounded-lg overflow-hidden bg-cream-cool shrink-0">
                            {product.images?.[0]?.url || product.images_json?.[0]?.url ? (
                              <img src={product.images?.[0]?.url || product.images_json?.[0]?.url} alt={product.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-forest-200 text-lg">🌿</div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-body text-body-sm font-medium text-forest-800 group-hover:text-gold-600 transition-colors line-clamp-1">{product.name}</p>
                            <p className="font-label text-label-sm text-forest-400">{product.price ? `₹${product.price}` : ''}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <Link
                      to={`/search?q=${encodeURIComponent(query)}`}
                      onClick={closeSearch}
                      className="mt-5 inline-flex items-center gap-2 font-label text-label-sm text-forest-800 hover:text-gold-600 transition-colors"
                    >
                      See all results for "{query}" →
                    </Link>
                  </motion.div>
                )}

                {/* No results */}
                {!loading && query.length >= 2 && results.length === 0 && (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6 text-center">
                    <p className="font-body text-body-md text-forest-400 mb-2">No results for "<span className="text-forest-700">{query}</span>"</p>
                    <p className="font-label text-label-sm text-forest-300">Try searching for an ingredient or concern instead.</p>
                  </motion.div>
                )}

                {/* Empty state — popular searches */}
                {!loading && !query && (
                  <motion.div key="popular" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <p className="font-label text-label-sm text-forest-400 uppercase tracking-widest mb-4">Popular Searches</p>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR.map((term) => (
                        <button
                          key={term}
                          onClick={() => handlePopular(term)}
                          className="h-9 px-4 rounded-full border border-cream-dark bg-cream text-forest-600 font-label text-label-sm hover:border-forest-500 hover:text-forest-900 transition-all duration-150"
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
